/**
 * F-86 phase 1: the SuperAdmin can suspend a community's entry of new people
 * and its new sales. The API decides everything: whether the community is
 * suspended (`suspended`, for everyone) and, only for the leadership (owner,
 * admin, moderator, finance) and the SuperAdmin, when and why. Nothing here
 * computes a role: the presence of `reason` is the server saying the viewer
 * may read it.
 */
export interface MemberEntryView {
  suspended: boolean
  suspendedAt?: string | null
  reason?: string | null
}

/** The 403 code every blocked action answers with. */
export const COMMUNITY_SUSPENDED = 'COMMUNITY_SUSPENDED'

/** Fired by the SDK when any request is refused because the community is suspended. */
export const COMMUNITY_SUSPENDED_EVENT = 'memberfy:community-suspended'

/** The SuperAdmin's reason: 10 to 2,000 characters, both to suspend and to resume. */
export const MEMBER_ENTRY_REASON_MIN = 10
export const MEMBER_ENTRY_REASON_MAX = 2000

/** Support contact shown to the leadership of a suspended community. */
export const SUPPORT_EMAIL = 'david@memberfy.net'

interface ApiErrorLike {
  response?: { status?: number; data?: { code?: string } }
}

/** Whether an API error is the refusal of a suspended community. */
export const isCommunitySuspendedError = (error: unknown): boolean =>
  (error as ApiErrorLike | null)?.response?.data?.code === COMMUNITY_SUSPENDED

/**
 * Tells the app that a request was refused because the community is
 * suspended, so the banner and the disabled actions reload the state (the
 * SuperAdmin may have switched it on while the page was open). Returns
 * whether it was that refusal.
 */
export const noticeSuspension = (error: unknown): boolean => {
  if (!isCommunitySuspendedError(error)) return false
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(COMMUNITY_SUSPENDED_EVENT))
  return true
}
